import Typography from '@mui/material/Typography';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection.tsx';
import { Table, TableName } from '@openx/components-table/Table.tsx';
import type { OrganizationAllowedProvider } from '@openx/types/organization.ts';
import {
  type AllTableCriteria,
  type CriteriaChange,
  CriteriaDimension,
  SortDirection,
} from '@openx/types/tableCriteria.ts';
import { clientSideCriteriaProcessing } from '@openx/utils/lib/clientSideCriteriaProcessing.ts';
import { applyCriteriaChange } from '@openx/utils/lib/tableCriteria.ts';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetTableColumns } from '../hooks/useGetTableColumns.tsx';

type LinkedProvidersProps = {
  linkedProviders?: OrganizationAllowedProvider[];
};

const initialCriteria: AllTableCriteria = {
  [CriteriaDimension.SORT]: {
    column: 'name',
    direction: SortDirection.ASC,
    sortableColumns: ['name', 'oa_match', 'bidstream', 'bidstream_plus'],
  },
};

export const LinkedProvidersSection = ({ linkedProviders }: LinkedProvidersProps) => {
  const { t } = useTranslation();
  const { linkedProvidersTableColumns } = useGetTableColumns();
  const [criteria, setCriteria] = useState<AllTableCriteria>(initialCriteria);

  const handleCriteriaChange = useCallback(
    (newCriteria: CriteriaChange<CriteriaDimension>) => {
      const newCriteriaProcessed = applyCriteriaChange(criteria, newCriteria);
      setCriteria(newCriteriaProcessed);
    },
    [criteria]
  );

  const mappedProviders = useMemo(() => {
    return clientSideCriteriaProcessing(linkedProviders ?? [], criteria);
  }, [linkedProviders, criteria]);

  return (
    <FormSection divider title={t('Linked Providers')}>
      {mappedProviders?.length ? (
        <Table
          name={TableName.LINKED_PROVIDERS}
          data={mappedProviders}
          onCriteriaChange={handleCriteriaChange}
          criteria={criteria}
          columns={linkedProvidersTableColumns}
          loading={false}
          enableTableHeaderCustomization={false}
        />
      ) : (
        <Typography data-test="no-linked-providers-message" variant="body2" color={'textSecondary'}>
          {t('There are no providers linked to this organization.')}
        </Typography>
      )}
    </FormSection>
  );
};
